'use client';

import Link from '@/components/LocaleLink';
import Image from 'next/image';
import Head from 'next/head';
import { useState, useEffect, Suspense } from 'react';
import { useSearchParams, usePathname } from 'next/navigation';
import { prefixPath } from '@/hooks/useLocalePath';
import { doesEmailExist, login } from '@/utils/user_service';
import { setLoginReturnUrl } from '@/magicLinkHandle';
import { FacebookIcon, GoogleIcon } from '@/components/icons';
import { signInClicked } from '../../../socialAuthHandles';
import { useTranslation } from '@/contexts/TranslationContext';

function SignInContent() {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { t } = useTranslation();
  const [form, setForm] = useState({
    email: null,
    password: null,
  });
  useEffect(() => {
    const returnUrl = searchParams.get('returnUrl');
    if (returnUrl) setLoginReturnUrl(returnUrl);
  }, [searchParams]);
  const handleInputChange = (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;
    setForm({
      ...form,
      [name]: value,
    });
  };

  // const submitForm = (event) => {
  //   event.preventDefault();
  //   let invalidCred = false;

  //   login(form);
  // };

  const submitForm = async (event) => {
    event.preventDefault();

    // Check if the email exists
    const emailExists = await doesEmailExist(form.email);

    if (emailExists) {
      // Email exists, proceed with login
      login(form);
    } else {
      // Email does not exist, redirect to signup page
        const returnUrl = searchParams.get('returnUrl');
      const signupUrl = returnUrl ? `/signup?returnUrl=${encodeURIComponent(returnUrl)}` : '/signup';
      alert(t('auth.emailNotFound'));
      window.location.href = prefixPath(pathname, signupUrl);
    }
  };

  return (
    <>
      <Head>
        <title>Growth charts and calculators for the whole family | NettleWorth</title>
        <meta name="title" content="Growth charts and calculators for the whole family | NettleWorth" />
        <meta name="description" content="Growth charts and calculators for the whole family | NettleWorth" />
      </Head>
      <main
      // className={poppins.className}
      >
        <div className="container-fluid signin-conatiner">
          <div className="row">
            <div className="col-12 col-md-5 p-0">
              <div className="left-panel">
                <h4>{t('auth.logIn')}</h4>
                <Image priority src="/assets/images/logos/NW-round.svg" height="350" width="350" alt="LM" />
              </div>
            </div>
            <div className="col-12 col-md-7">
              <div className="row">
                <div className="col-12 col-lg-9 mx-auto text-center py-3 py-md-5 px-lg-0">
                  <h3 className="d-none d-lg-block">{t('auth.benefits')}</h3>

                  <div className="d-none d-lg-flex my-3 benefits-section">
                    <div>
                      <Image priority src="/assets/images/icons/reminder.svg" alt={t('aria.icon')} height="35" width="35" />
                      <h6>
                        {t('auth.benefits.reminders')} <br /> {t('auth.benefits.reminders2')}
                      </h6>
                    </div>
                    <div>
                      <Image priority src="/assets/images/icons/chart_growth.svg" alt={t('aria.icon')} height="35" width="35" />
                      <h6>
                        {t('auth.benefits.chartGrowth')} <br /> {t('auth.benefits.chartGrowth2')}
                      </h6>
                    </div>
                    <div>
                      <Image priority src="/assets/images/icons/family.svg" alt={t('aria.icon')} height="35" width="35" />
                      <h6>
                        {t('auth.benefits.addFamily')} <br /> {t('auth.benefits.addFamily2')}
                      </h6>
                    </div>
                  </div>

                  <div className="row">
                    <h2 className="mb-4 d-none d-lg-block">{t('auth.logIn')}</h2>
                    <div md="6" className="col-12 col-md-6 d-flex gap-3 justify-content-center w-100">
                      <button className="social-login-btn" onClick={() => signInClicked('google')}>
                        <GoogleIcon />
                        {t('auth.signInWithGoogle')}
                      </button>
                      <button className="social-login-btn" onClick={() => signInClicked('facebook')}>
                        <FacebookIcon />
                        {t('auth.signInWithFacebook')}
                      </button>
                    </div>
                    <div className="or-div my-4">
                      <span>{t('auth.or')}</span>
                    </div>
                    <form onSubmit={submitForm} className="position-relative">
                      <div className="col-12">
                        <label className="d-block text-start">{t('auth.email')}</label>
                        <input
                          name="email"
                          placeholder={t('auth.emailPlaceholder')}
                          type="email"
                          className="form-control no-border email"
                          onChange={handleInputChange}
                          required
                        />
                        <img className="email-icon" src="/assets/images/icons/email.svg" alt={t('aria.emailIcon')} />
                      </div>
                      <div className="col-12 my-lg-5 my-5">
                        <p className="text-center guide-text">
                          {t('auth.magicLinkIntro')}{' '}
                          <strong>{form.email ? form.email : t('auth.emailPlaceholder')}</strong>
                        </p>
                        <p className="mt-3 text-center guide-text">
                          {t('auth.magicLinkSignIn').split('{action}')[0]}
                          <strong>{t('auth.logIn')}</strong>
                          {t('auth.magicLinkSignIn').split('{action}')[1]}
                        </p>
                      </div>
                      <div className="col-12">
                        <button type="submit" className="btn btn-primary btn-lg btn-yello">
                          {t('auth.logIn')}
                        </button>
                      </div>
                    </form>
                    <p className="my-4" style={{ fontWeight: '400' }}>
                      {t('auth.dontHaveAccount')}
                      <Link
                        prefetch={false}
                        href={searchParams.get('returnUrl') ? `/signup?returnUrl=${encodeURIComponent(searchParams.get('returnUrl'))}` : '/signup'}
                        style={{ color: '#FFB727', coursor: 'pointer' }}
                      >
                        {' '}
                        {t('auth.signUp')}
                      </Link>
                    </p>
                    <Link prefetch={false} href="/" className="home-link mt-1 mb-3">
                      {t('auth.homepageLink')}
                    </Link>
                    <div className="d-flex d-lg-none my-3 benefits-section">
                      <div>
                        <Image priority src="/assets/images/icons/reminder.svg" alt={t('aria.icon')} height="35" width="35" />
                        <h6>
                          {t('auth.benefits.reminders')} <br /> {t('auth.benefits.reminders2')}
                        </h6>
                      </div>
                      <div>
                        <Image
                          priority
                          src="/assets/images/icons/chart_growth.svg"
                          alt={t('aria.icon')}
                          height="35"
                          width="35"
                        />
                        <h6>
                          {t('auth.benefits.chartGrowth')} <br /> {t('auth.benefits.chartGrowth2')}
                        </h6>
                      </div>
                      <div>
                        <Image priority src="/assets/images/icons/family.svg" alt={t('aria.icon')} height="35" width="35" />
                        <h6>
                          {t('auth.benefits.addFamily')} <br /> {t('auth.benefits.addFamily2')}
                        </h6>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

function SignInFallback() {
  const { t } = useTranslation();
  return (
    <main>
      <div className="container-fluid signin-conatiner">
        <div className="row">
          <div className="col-12 col-md-5" style={{ backgroundColor: '#25AF9B' }}>
            <div className="left-panel">
              <h4>{t('auth.logIn')}</h4>
              <Image priority src="/assets/images/logos/LM-round.svg" height="350" width="350" alt="LM" />
            </div>
          </div>
          <div className="col-12 col-md-7 d-flex align-items-center justify-content-center py-5">
            <p className="text-muted">{t('auth.loading')}</p>
          </div>
        </div>
      </div>
    </main>
  );
}

export default function SignInPage() {
  return (
    <Suspense fallback={<SignInFallback />}>
      <SignInContent />
    </Suspense>
  );
}
